import client from "services/ngage/api-client";
import {Contact, ContactData} from "types/Contact";
import deserializeContact from "services/ngage/deserializers/contact";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";
import AgeGroup from "../../types/AgeGroup";

type ContactFilters = {
    owner?: number | string | null
    ageGroup?: AgeGroup | null
    search?: string | null
    sort?: string | null
    page?: number
    perPage?: number
}

/**
 * Get all contacts (paginated)
 */
const filtered = async (filters: ContactFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    try {
        const page = (await client.get<DataResource<Contact> | null>('/contacts', {params})).data

        if (page) page.data = page.data.map(deserializeContact)

        return page
    } catch (error) {
        console.error("Unable to fetch contacts", error)
        return null
    }
}

/**
 * Get a single contact
 *
 * @param {number} contactId
 */
const get = async (contactId: number) => {
    try {
        const contact = (await client.get<Contact | null>(`/contacts/${contactId}`)).data
        return contact ? deserializeContact(contact) : null
    } catch (error) {
        console.error(`Unable to fetch contact ${contactId}`, error)
        return null
    }
}

/**
 * Store a new contact
 *
 * @param data
 */
const store = async (data: ContactData) => {
    try {
        const contact = (await client.post<Contact | null>('/contacts', data)).data
        return contact ? deserializeContact(contact) : null
    } catch (error) {
        console.error("Unable to store contact", error)
        throw error
    }
}

/**
 * Update an existing contact
 *
 * @param {number} contactId
 * @param data
 */
const update = async (contactId: number, data: ContactData) => {
    try {
        const contact = (await client.put<Contact | null>(`/contacts/${contactId}`, data)).data
        return contact ? deserializeContact(contact) : null
    } catch (error) {
        console.error(`Unable to update contact ${contactId}`, error)
        throw error
    }
}

/**
 * Permanently delete a contact
 *
 * @param {number} contactId
 */
const destroy = async (contactId: number) => {
    try {
        return (await client.delete<null>(`/contacts/${contactId}`)).status === 204
    } catch (error) {
        console.error(`Unable to delete contact ${contactId}`, error)
        throw error
    }
}

const contacts = {
    filtered,
    get,
    store,
    update,
    destroy
}

export default contacts;